// __EQUIP_COMPAT__ (2026-10-04) Equipment types + the ONE driver/equipment
// compatibility check.
//
// Henry, 2026-10-04: "We need to make sure all equipment variables like
// dimensions, weights etc. are honoured per all equipment types. Driver and
// equipment units, or unit pairs like tractor plus trailer pairing, must work for
// this, so when a job or route of jobs is assigned to a driver, a hard warning
// comes up about equipment incompatibility."
//
// TWIN: Billing-Manager equipment_compat.py runs the same rules for the hub's
// booking form and the dispatch app. Both run the shared cases in
// Billing-Manager/tests/equipment_compat_cases.json (sim/equipment.test.mjs here,
// tests/equipment_compat_test.py there) - change one, change both.
//
// UNITS. Equipment columns are honest metric (trucks/trailers *_kg, *_cm). Job
// columns are NOT: loads.weight_kg holds POUNDS and loads.length_cm / width_cm /
// height_cm hold FEET (the hub writes them that way). Everything here works in
// feet and pounds; equipment is converted on the way in.
//
// Pure: no Supabase, no React.

export const LB_PER_KG = 2.20462262;
export const FT_PER_CM = 1 / 30.48;

// ---------------------------------------------------------------- types
// class: what the unit can carry and how it is measured.
//   tractor  - pulls a trailer, carries nothing itself
//   open     - open deck (flatbed / tilt / step deck / lowboy / gooseneck...): deck L x W,
//              deck height from the ground; travel height = deck height + cargo height
//   enclosed - box / van / reefer: inside L x W x H
//   pickup   - pickup box: bed L x W
//   bulk     - dump / tanker / hopper / livestock: weight only, no piece sizes
export const EQUIPMENT_CLASSES = {
  tractor: 'Tractor (pulls a trailer)',
  open: 'Open deck',
  enclosed: 'Enclosed (box / van)',
  pickup: 'Pickup box',
  bulk: 'Bulk (dump / tank / hopper)',
};

// deckHeightFt: a sensible starting value offered on a new record (not written
// to an existing one). features: pre-ticked on a new record of that type.
export const TRUCK_TYPES = {
  day_cab:        { label: 'Tractor - day cab', cls: 'tractor' },
  sleeper:        { label: 'Tractor - sleeper', cls: 'tractor' },
  straight_truck: { label: 'Straight truck (flat deck)', cls: 'open', deckHeightFt: 4 },
  flatbed:        { label: 'Flatbed truck', cls: 'open', deckHeightFt: 4.5 },
  tilt_deck:      { label: 'Tilt deck / rollback', cls: 'open', deckHeightFt: 3.5, features: ['tilt', 'winch'] },
  box_truck:      { label: 'Box truck / cube van', cls: 'enclosed' },
  dump_truck:     { label: 'Dump truck', cls: 'bulk' },
  tanker:         { label: 'Tank truck', cls: 'bulk' },
  pickup:         { label: 'Pickup', cls: 'pickup' },
  hotshot:        { label: 'Hotshot pickup (pulls a gooseneck)', cls: 'tractor', features: ['gooseneck_hitch'] },
  curtain_side:   { label: 'Curtain-side straight truck', cls: 'enclosed', features: ['side_loading'] },
  roll_tarp:      { label: 'Rolling-tarp straight truck', cls: 'enclosed', features: ['top_loading'] },
  crane_truck:    { label: 'Straight truck with on-board crane', cls: 'open', deckHeightFt: 5, features: ['crane', 'self_unload'] },
  other:          { label: 'Other', cls: 'open' },
};

export const TRAILER_TYPES = {
  flatbed:    { label: 'Flatbed', cls: 'open', deckHeightFt: 5 },
  step_deck:  { label: 'Step deck', cls: 'open', deckHeightFt: 3.5 },
  lowboy:     { label: 'Lowboy / float', cls: 'open', deckHeightFt: 2, features: ['ramps'] },
  tilt:       { label: 'Tilt trailer', cls: 'open', deckHeightFt: 2.5, features: ['tilt'] },
  gooseneck:  { label: 'Gooseneck', cls: 'open', deckHeightFt: 3, features: ['ramps'] },
  deck_over:  { label: 'Deck-over', cls: 'open', deckHeightFt: 3.2 },
  utility:    { label: 'Utility / equipment trailer', cls: 'open', deckHeightFt: 1.8, features: ['ramps'] },
  car_hauler: { label: 'Car hauler', cls: 'open', deckHeightFt: 1.8, features: ['ramps', 'winch'] },
  dry_van:    { label: 'Dry van', cls: 'enclosed' },
  reefer:     { label: 'Reefer (refrigerated)', cls: 'enclosed' },
  dump:       { label: 'Dump', cls: 'bulk' },
  hopper:     { label: 'Hopper', cls: 'bulk' },
  tanker:     { label: 'Tanker', cls: 'bulk' },
  livestock:  { label: 'Livestock', cls: 'bulk' },
  // __EQUIP_R5__ (2026-10-05) Henry: Isaac / Samuel's Eby bumper-pull 24x8x10 with ramps
  bumper_pull: { label: 'Bumper-pull deck trailer', cls: 'open', deckHeightFt: 2.5, features: ['ramps'] },
  other:      { label: 'Other', cls: 'open' },
};

export const EQUIPMENT_FEATURES = {
  ramps: 'Loading ramps',
  tilt: 'Tilt / hydraulic tilt deck',
  beavertail: 'Beavertail',
  winch: 'Winch',
  liftgate: 'Liftgate',
  crane: 'Crane / knuckle boom',
  tarps: 'Tarps',
  chains: 'Chains & binders',
  straps: 'Straps',
  // __FLEET_2026_10_04__ how it can be loaded / what it can pull (Henry: "no overhead crane
  // loading, good for dock loading"). An open deck always counts as top- and side-loadable.
  dock_height: 'Dock-height deck (dock loading)',
  top_loading: 'Loads from above (overhead crane)',
  side_loading: 'Loads from the side (forklift)',
  fifth_wheel: 'Fifth wheel',
  gooseneck_hitch: 'Gooseneck hitch',
  self_unload: 'On-board crane / self-unload',
  // __EQUIP_ASSIGN__ what else it can pull (Equipment Assignment pairing rules)
  gooseneck_adapter: 'Gooseneck adapter on the fifth wheel',
  bumper_hitch: 'Bumper / pintle hitch',
  // __EQUIP_R6__ (2026-10-05) Henry: p01 "has BOTH couplers. It works as a gooseneck and as a fifth wheel."
  convertible_coupler: 'Fifth-wheel kingpin + gooseneck coupler (convertible)',
  // __TRUCK_REQUIRED__ (2026-10-06) the only thing that makes a unit a "closed truck"
  enclosed_body: 'Enclosed / box body (closed truck)',
};
// features that only make sense on a trailer (hidden on the truck form)
export const TRAILER_ONLY_FEATURES = ['convertible_coupler'];

// __TRUCK_REQUIRED__ (2026-10-06) Henry: a booking-form flag "to specify whether a tarp truck or a
// closed truck is required" (hub booking form + dispatch app quoting tab). Kept on the job as
// billing_data.truck_required = 'tarp' | 'closed' (absent = none).
//   tarp   - met by a rolling-tarp or curtain-side body (truck / trailer type roll_tarp, curtain_side)
//   closed - met ONLY by an enclosed / box body: the enclosed_body feature on the unit that carries
//            the load, or a box truck / dry van / reefer type. Curtain side counts as tarp only.
// Not met -> the 'truck_required' issue: a warning like the other limits (Settings can make it Block).
//
// __SITE_LOADING__ (2026-10-17) Henry: "REMOVE Closed truck (no vans). Covered = TARP with two kinds: Rolling tarp
// (loads every way) and Curtain side (hard roof: can NOT be loaded by overhead crane, only side / dock)." The only
// requirement is now 'tarp'; an old 'closed' (or 'enclosed', 'box truck' ...) on a job reads as 'tarp'.
//
// __OPEN_DECK__ (2026-10-27) Henry: "Open should be its own requirement ... broaden that to hauling and all other places."
// Every truck-required picker now offers the same three choices (twin of equipment_compat.py, which got them first
// with the customer portal's __ANY_TRUCK__):
//   Any truck        - '' (no requirement; the office form's old "None"): any road-ready unit
//   Open deck truck  - 'open': an open deck only - NOT a rolling tarp, curtain side or enclosed body (HMGC: t03 curtain
//                      side, t04 / t05 rolling tarp are excluded); the crane truck is an open deck
//   Tarp truck       - 'tarp': a rolling-tarp or curtain-side body
// sim/opendeck.test.mjs fails when this table and the hub's drift apart.
export const TRUCK_REQUIRED = { tarp: 'Tarp truck', open: 'Open deck truck' };
export const TRUCK_REQUIRED_WHAT = { tarp: 'a rolling-tarp or curtain-side body', open: 'an open deck, no tarp or curtain body' };
export const TRUCK_ANY_LABEL = 'Any truck';
// the picker, in the order every form shows it ('' = Any truck)
export const TRUCK_CHOICES = [['', 'Any truck'], ['open', 'Open deck truck (no tarp or curtain)'], ['tarp', 'Tarp truck (rolling tarp or curtain side)']];
export const TARP_BODY_TYPES = ['roll_tarp', 'curtain_side'];
export const CLOSED_BODY_TYPES = [];   // retired with 'closed'
const TR_ALIASES = { tarp: 'tarp', tarptruck: 'tarp', tarped: 'tarp', closed: 'tarp', closedtruck: 'tarp', enclosed: 'tarp',
  enclosedtruck: 'tarp', rollingtarp: 'tarp', rolltarp: 'tarp', curtainside: 'tarp', covered: 'tarp',
  open: 'open', opendeck: 'open', opendecktruck: 'open', opentruck: 'open' };   // __OPEN_DECK__
// __SITE_LOADING__ "The crane truck is an OPEN truck only: in every booking form choosing Tarp + truck crane together is
// impossible; show why." One reason text for every form.
export const TARP_CRANE_WHY = "A tarp truck can't also be the truck crane: our crane truck is an open truck (no tarp or curtain). "
  + 'Choose Tarp truck OR Truck crane self-unload, not both.';
// 'tarp' | 'open' | '' from a stored / sent value ('tarp', 'Tarp truck', 'Open deck truck', the retired 'closed' = tarp)
export const truckRequiredValue = (v) => TR_ALIASES[String(v || '').toLowerCase().replace(/[^a-z]/g, '')] || '';
// The requirement on a job row: its own truck_required, else billing_data.truck_required
// (billing_data may be stored as a JSON string).
export function truckRequiredOf(row) {
  const r = row || {};
  let v = r.truck_required || r.truckRequired;
  if (!v) {
    let bd = r.billing_data;
    if (typeof bd === 'string') { try { bd = JSON.parse(bd); } catch (e) { bd = null; } }
    if (bd && typeof bd === 'object') v = bd.truck_required;
  }
  return truckRequiredValue(v);
}
// Does the unit that carries the load (unitCapacity) give the body the job needs?
export function meetsTruckRequired(cap, req) {
  const c = cap || {};
  if (req === 'tarp') return TARP_BODY_TYPES.includes(c.type);
  // __OPEN_DECK__ an open deck: not a tarp / curtain body, not an enclosed / box body
  if (req === 'open') return c.cls === 'open' && !TARP_BODY_TYPES.includes(c.type) && !(c.features || []).includes('enclosed_body');
  return true;
}
// __OPEN_DECK__ the words for a stored value ('' = Any truck)
export const truckLabel = (v) => TRUCK_REQUIRED[truckRequiredValue(v)] || TRUCK_ANY_LABEL;
// __OPEN_DECK__ a driver list's mark (Planning's ASSIGN picker): the driver's unit on that day against the job's Truck
// required -> '' (met, no requirement, or no unit to tell) | the warning. Assigning still goes through the equipment check
// (EquipmentGate: warn + Assign anyway, or Block when Settings says so); this only shows it before the pick.
export function truckRequiredHint({ driverId, row, fleet, rules, day }) {
  const req = truckRequiredOf(row);
  if (!req || !driverId || !fleet || fleet.error) return '';
  const unit = resolveUnit({ driverId, fleet, day });
  const cap = unit && unit.truck ? unitCapacity(unit, rules, day) : null;
  if (!cap || meetsTruckRequired(cap, req)) return '';
  return 'Needs ' + articled(TRUCK_REQUIRED[req]) + ' (' + TRUCK_REQUIRED_WHAT[req] + '); ' + cap.label + ' is ' + articled(cap.typeLabel) + '.';
}

// __SITE_LOADING__ (2026-10-17) how a site loads (address book "Loading at this site", multi-select; none = unspecified,
// never a warning) against the unit's body / cover. The matrix (Settings > Equipment rules > Site loading) says which
// loading methods each body / cover takes; a row can be added for any truck / trailer type. Twin of equipment_compat.py.
export const LOADING_METHODS = { dock: 'Dock', overhead_crane: 'Overhead crane', yard_lift: 'Yard lift truck' };
export const COVER_TYPES = { open: 'Open deck', roll_tarp: 'Rolling tarp', curtain_side: 'Curtain side' };
export const DEFAULT_LOADING_MATRIX = { open: ['dock', 'overhead_crane', 'yard_lift'], roll_tarp: ['dock', 'overhead_crane', 'yard_lift'],
  curtain_side: ['dock', 'yard_lift'] };
const LM_ALIASES = { overhead: 'overhead_crane', crane: 'overhead_crane', yard_lift_truck: 'yard_lift', forklift: 'yard_lift',
  lift_truck: 'yard_lift', loading_dock: 'dock' };
export function loadingMethods(v) {
  const arr = typeof v === 'string' ? v.split(',') : (Array.isArray(v) ? v : []);
  const keys = new Set();
  arr.forEach((x) => {
    let k = String(x == null ? '' : x).trim().toLowerCase().replace(/[^a-z]+/g, '_').replace(/^_+|_+$/g, '');
    k = LM_ALIASES[k] || k;
    if (LOADING_METHODS[k]) keys.add(k);
  });
  return Object.keys(LOADING_METHODS).filter((k) => keys.has(k));
}
export const tarpCraneConflict = (truckRequired, selfUnload) => (truckRequiredValue(truckRequired) === 'tarp'
  && selfUnloadValue(selfUnload) === 'crane' ? TARP_CRANE_WHY : '');
// The matrix row for the unit that carries the load: its own type when the matrix has a row for it, else 'open' for any
// open deck; null = not in the matrix (not checked).
export function coverOf(cap, matrix) {
  const m = matrix && typeof matrix === 'object' ? matrix : DEFAULT_LOADING_MATRIX;
  const c = cap || {};
  if (c.type && Object.prototype.hasOwnProperty.call(m, c.type)) return c.type;
  if (c.cls === 'open' && Object.prototype.hasOwnProperty.call(m, 'open')) return 'open';
  return null;
}
// The equipment form's "Body / cover": open / roll_tarp / curtain_side from a truck type ('' = not one of the three)
export function bodyCoverOf(kind, type) {
  if (type === 'roll_tarp' || type === 'curtain_side') return type;
  return typeInfo(kind, type).cls === 'open' ? 'open' : '';
}
// ...and the truck type a cover choice gives (open keeps an open type; the crane truck stays the crane truck)
export function typeForCover(type, cover) {
  if (cover === 'roll_tarp' || cover === 'curtain_side') return cover;
  if (cover === 'open') return typeInfo('truck', type).cls === 'open' ? type : 'straight_truck';
  return type;
}
// A tarp / curtain body with a crane: impossible (the crane truck is open) -> the reason, else ''
export function coverCraneConflict(type, features) {
  const f = Array.isArray(features) ? features : [];
  return (type === 'roll_tarp' || type === 'curtain_side') && (f.includes('crane') || f.includes('self_unload'))
    ? 'A crane truck is an open truck: a rolling-tarp or curtain-side body can\'t have the crane. Untick the crane, or set Body / cover to Open.' : '';
}
const STREET_WORDS = { road: 'rd', street: 'st', avenue: 'ave', drive: 'dr', boulevard: 'blvd', crescent: 'cres', court: 'ct',
  highway: 'hwy', place: 'pl', lane: 'ln', east: 'e', west: 'w', north: 'n', south: 's', concession: 'conc', sideroad: 'sdrd', township: 'twp' };
const SITE_SUFFIX = ['inc', 'incorporated', 'ltd', 'limited', 'corp', 'corporation', 'co', 'llc'];
export function siteNameKey(text) {
  const w = String(text == null ? '' : text).toLowerCase().replace(/&/g, ' and ').replace(/'/g, '').split(/[^a-z0-9]+/).filter(Boolean);
  while (w.length > 1 && SITE_SUFFIX.includes(w[w.length - 1])) w.pop();
  return w.join('');
}
export function siteStreetKey(text) {
  const parts = String(text == null ? '' : text).toLowerCase().replace(/\b[a-z]\d[a-z]\s?\d[a-z]\d\b/g, '').split(',').map((x) => x.trim());
  const k = (t) => t.split(/[^a-z0-9]+/).filter(Boolean).map((x) => STREET_WORDS[x] || x).join('');
  for (let i = 0; i < parts.length; i++) {
    if (/^\d+[a-z]?\b/.test(parts[i])) return [k(parts[i]), i + 1 < parts.length ? k(parts[i + 1]) : ''];
  }
  return ['', ''];
}
export function cleanSites(raw) {
  const out = [];
  (Array.isArray(raw) ? raw : []).forEach((x) => {
    if (!x || typeof x !== 'object') return;
    const meth = loadingMethods(x.loading);
    const name = String(x.name || x.display_name || x.label || '').split(/\s+/).filter(Boolean).join(' ').slice(0, 120);
    const addr = String(x.address || '').trim().slice(0, 300);
    if (!meth.length || !(name || addr)) return;
    out.push({ name: name || addr, address: addr,
      aliases: (Array.isArray(x.aliases) ? x.aliases : []).filter((a) => String(a || '').trim()).map((a) => String(a).trim().slice(0, 120)).slice(0, 20),
      loading: meth });
  });
  return out.slice(0, 2000);
}
// __TELEMATICS__ per truck: {provider, vehicle_id, device_serial, positions, readings} (Trucks > Edit > ELD / telematics)
export function cleanTelematicsUnits(raw) {
  const out = {};
  Object.keys(raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {}).forEach((k) => {
    const v = raw[k];
    if (!k || !v || typeof v !== 'object' || Array.isArray(v)) return;
    const prov = String(v.provider || '').toLowerCase().replace(/[^a-z0-9_]/g, '').slice(0, 20);
    const vid = String(v.vehicle_id || '').split(/\s+/).filter(Boolean).join(' ').slice(0, 80);
    const ser = String(v.device_serial || '').split(/\s+/).filter(Boolean).join(' ').slice(0, 40);
    if (!prov || !(vid || ser)) return;
    out[String(k)] = { provider: prov, vehicle_id: vid, device_serial: ser, positions: v.positions === true, readings: v.readings !== false };
  });
  return out;
}
// __TELEMATICS__ what the hub mirrors per truck (odometer, engine hours, open faults ...): kept as written
export function cleanTelematicsReadings(raw) {
  const out = {};
  Object.keys(raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {}).forEach((k) => {
    if (k && raw[k] && typeof raw[k] === 'object' && !Array.isArray(raw[k])) out[String(k)] = raw[k];
  });
  return out;
}
// __OPERATING_LIMITS__ the address book's names and addresses (mirrored by the hub): the home-point picker's list
export function cleanPlaces(raw) {
  const out = [];
  const seen = new Set();
  (Array.isArray(raw) ? raw : []).forEach((e) => {
    if (!e || typeof e !== 'object') return;
    const name = String(e.name || e.display_name || e.label || '').split(/\s+/).filter(Boolean).join(' ').slice(0, 120);
    const addr = String(e.address || '').split(/\s+/).filter(Boolean).join(' ').slice(0, 300);
    if (!addr || seen.has(name + '|' + addr)) return;
    seen.add(name + '|' + addr);
    out.push({ name: name || addr, address: addr });
  });
  return out.slice(0, 1500);
}
export function matchSite(text, sites, placeName) {
  const [st, city] = siteStreetKey(text);
  const names = new Set([siteNameKey(text)]);
  if (placeName) names.add(siteNameKey(placeName));
  const first = String(text == null ? '' : text).split(',')[0];
  if (!/^\s*\d/.test(first)) names.add(siteNameKey(first));
  const nm = [...names].filter((n) => n.length >= 4);
  for (const s of (sites || [])) {
    if (st) {
      const [sst, scity] = siteStreetKey(s.address);
      if (sst && sst === st && (!city || !scity || city === scity)) return s;
    }
    if (nm.length) {
      const keys = [s.name, ...(s.aliases || [])].map(siteNameKey);
      if (nm.some((n) => keys.includes(n))) return s;
    }
  }
  return null;
}
function jobStopTexts(row, stops) {
  const r = row || {};
  const out = [];
  ['pickup_address', 'delivery_address'].forEach((k) => { if (r[k]) out.push([String(r[k]), null]); });
  (Array.isArray(r.addresses) ? r.addresses : []).forEach((a) => {
    if (a && typeof a === 'object') {
      const t = a.address || a.text || a.value || '';
      if (t || a.place_name) out.push([String(t), a.place_name || a.name || null]);
    } else if (String(a == null ? '' : a).trim()) out.push([String(a), null]);
  });
  (Array.isArray(r.load_stops) ? r.load_stops : []).forEach((s) => {
    if (s && typeof s === 'object' && (s.address || s.place_name)) out.push([String(s.address || ''), s.place_name || null]);
  });
  (stops || []).forEach((s) => {
    if (s && typeof s === 'object' && r.id && s.load_id === r.id && (s.address || s.place_name)) out.push([String(s.address || ''), s.place_name || null]);
  });
  return out;
}
// [[site name, message]] for the stops of one job whose site loads only in ways the unit's body / cover can't take
// __OPERATING_LIMITS__ (2026-10-17) per unit "No city runs" (city zones: city + radius or a polygon; presets editable in
// Settings > Equipment rules) and "Max radius from home point" (km). Checked at every stop with a location; a stop with no
// location yet says so (info). Twin of equipment_compat.py (shared cases).
export const CITY_PRESETS = [
  { key: 'toronto', name: 'Toronto', lat: 43.7001, lng: -79.4163, radius_km: 16.0 },
  { key: 'kitchener_waterloo', name: 'Kitchener-Waterloo', lat: 43.4550, lng: -80.5000, radius_km: 10.0 },
  { key: 'guelph', name: 'Guelph', lat: 43.5448, lng: -80.2482, radius_km: 8.0 },
  { key: 'hamilton', name: 'Hamilton', lat: 43.2400, lng: -79.8500, radius_km: 12.0 },
  { key: 'london', name: 'London', lat: 42.9849, lng: -81.2453, radius_km: 11.0 },
];
const latOk = (v) => { const n = num(v); return n !== null && n >= -90 && n <= 90 ? n : null; };
const lngOk = (v) => { const n = num(v); return n !== null && n >= -180 && n <= 180 ? n : null; };
export function cleanZones(raw) {
  const out = []; const seen = new Set();
  (Array.isArray(raw) ? raw : []).slice(0, 60).forEach((z) => {
    if (!z || typeof z !== 'object') return;
    const name = String(z.name || '').split(/\s+/).filter(Boolean).join(' ').slice(0, 60);
    const key = String(z.key || name).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40);
    if (!key || seen.has(key)) return;
    let poly = [];
    (Array.isArray(z.polygon) ? z.polygon : []).slice(0, 300).forEach((pt) => {
      if (Array.isArray(pt) && pt.length >= 2 && latOk(pt[0]) !== null && lngOk(pt[1]) !== null) poly.push([Number(pt[0]), Number(pt[1])]);
    });
    const lat = latOk(z.lat); const lng = lngOk(z.lng); const r = pos(z.radius_km);
    if (poly.length < 3) {
      poly = [];
      if (lat === null || lng === null || r === null || r > 300) return;
    }
    seen.add(key);
    out.push({ key, name: name || key, lat, lng, radius_km: r, polygon: poly });
  });
  return out;
}
export function cleanUnitLimits(raw) {
  const out = {};
  Object.keys(raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {}).forEach((k) => {
    const v = raw[k];
    if (!k || !v || typeof v !== 'object') return;
    const h = v.home && typeof v.home === 'object' ? v.home : null;
    let home = null;
    if (h && latOk(h.lat) !== null && lngOk(h.lng) !== null) {
      home = { label: String(h.label || '').split(/\s+/).filter(Boolean).join(' ').slice(0, 80), address: String(h.address || '').trim().slice(0, 300),
        lat: Number(h.lat), lng: Number(h.lng) };
    }
    const r = pos(v.max_radius_km);
    const lim = { no_city: v.no_city === true,
      cities: (Array.isArray(v.cities) ? v.cities : []).filter((c) => String(c == null ? '' : c).trim()).map((c) => String(c).toLowerCase().replace(/[^a-z0-9_]/g, '').slice(0, 40)).slice(0, 60),
      max_radius_km: r !== null && r <= 5000 ? r : null, home };
    if (lim.no_city || lim.max_radius_km || lim.home) out[String(k)] = lim;
  });
  return out;
}
export function haversineKm(a, b) {
  const rad = (d) => d * Math.PI / 180;
  const la1 = rad(a[0]); const lo1 = rad(a[1]); const la2 = rad(b[0]); const lo2 = rad(b[1]);
  const h = Math.sin((la2 - la1) / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin((lo2 - lo1) / 2) ** 2;
  return 2 * 6371.0088 * Math.asin(Math.min(1, Math.sqrt(h)));
}
export function inPolygon(pt, poly) {
  const y = pt[0]; const x = pt[1]; let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const yi = poly[i][0]; const xi = poly[i][1]; const yj = poly[j][0]; const xj = poly[j][1];
    if ((yi > y) !== (yj > y) && x < (xj - xi) * (y - yi) / ((yj - yi) || 1e-12) + xi) inside = !inside;
  }
  return inside;
}
export function zoneOf(pt, zones) {
  for (const z of (zones || [])) {
    if (z.polygon && z.polygon.length) { if (inPolygon(pt, z.polygon)) return z; }
    else if (z.lat !== null && z.lat !== undefined && haversineKm(pt, [z.lat, z.lng]) <= (z.radius_km || 0) + 1e-9) return z;
  }
  return null;
}
const numTxt = (n) => String(Number(n));
export function jobPoints(row, stops) {
  const r = row || {}; const out = [];
  const put = (lat, lng, label) => {
    const la = latOk(lat); const lo = lngOk(lng);
    if (la !== null && lo !== null && !(la === 0 && lo === 0)) out.push([String(label || '').split(',')[0].slice(0, 60) || 'a stop', la, lo]);
  };
  (Array.isArray(r.load_stops) ? r.load_stops : []).forEach((s) => {
    if (s && typeof s === 'object') put(s.latitude !== undefined ? s.latitude : s.lat, s.longitude !== undefined ? s.longitude : s.lng, s.place_name || s.address);
  });
  (Array.isArray(r.points) ? r.points : []).forEach((p) => {
    if (p && typeof p === 'object' && !Array.isArray(p)) put(p.lat, p.lng, p.label || p.address);
    else if (Array.isArray(p) && p.length >= 2) put(p[0], p[1], p.length > 2 ? p[2] : '');
  });
  (Array.isArray(r.addresses) ? r.addresses : []).forEach((a) => {
    if (a && typeof a === 'object' && a.lat !== null && a.lat !== undefined) put(a.lat, a.lng, a.place_name || a.address);
  });
  (stops || []).forEach((s) => {
    if (s && typeof s === 'object' && r.id && s.load_id === r.id) put(s.latitude !== undefined ? s.latitude : s.lat, s.longitude !== undefined ? s.longitude : s.lng, s.place_name || s.address);
  });
  const seen = new Set(); const uniq = [];
  out.forEach((x) => { const k = x[1].toFixed(5) + ',' + x[2].toFixed(5); if (!seen.has(k)) { seen.add(k); uniq.push(x); } });
  return uniq;
}
export function operatingIssues(cap, jobRow, rules, stops) {
  const R = rules || {};
  const lim = (R.unit_limits || {})[String((cap || {}).truckId || '')];
  if (!lim || !(lim.no_city || lim.max_radius_km)) return [];
  const unit = (cap || {}).label || 'this unit';
  const pts = jobPoints(jobRow, stops);
  if (!pts.length) return [['limit_unchecked', 'the stop locations are not known yet - ' + unit + "'s city / distance limits are not checked"]];
  const out = []; const seen = new Set();
  if (lim.no_city) {
    const zones = (R.city_zones || []).filter((z) => !(lim.cities || []).length || lim.cities.includes(z.key));
    pts.forEach(([place, la, lo]) => {
      const z = zoneOf([la, lo], zones);
      if (z && !seen.has(z.key)) { seen.add(z.key); out.push(['city_run', 'a stop is in ' + z.name + ' (' + place + '); ' + unit + ' does no city runs']); }
    });
  }
  const r = lim.max_radius_km;
  if (r) {
    const home = lim.home;
    if (!home) out.push(['limit_unchecked', unit + ' has a ' + numTxt(r) + ' km limit but no home point - set it on the truck']);
    else {
      let far = pts[0]; let d = haversineKm([home.lat, home.lng], [far[1], far[2]]);
      pts.forEach((x) => { const dd = haversineKm([home.lat, home.lng], [x[1], x[2]]); if (dd > d) { d = dd; far = x; } });
      if (d > r + 1e-9) out.push(['beyond_radius', 'a stop is ' + Math.floor(d + 0.5) + ' km from ' + unit + "'s home point (" + (home.label || home.address || 'home') + ') - its limit is ' + numTxt(r) + ' km (' + far[0] + ')']);
    }
  }
  return out;
}

export function siteLoadingIssues(cap, jobRow, rules, stops) {
  const R = rules || {};
  const matrix = R.loading_matrix && typeof R.loading_matrix === 'object' ? R.loading_matrix : DEFAULT_LOADING_MATRIX;
  const sites = R.sites || [];
  const cover = coverOf(cap, matrix);
  if (!sites.length || cover === null) return [];
  const ok = new Set(matrix[cover] || []);
  const out = []; const seen = new Set();
  jobStopTexts(jobRow, stops).forEach(([text, pn]) => {
    const s = matchSite(text, sites, pn);
    if (!s || seen.has(s.name)) return;
    seen.add(s.name);
    const meth = s.loading || [];
    if (meth.length && !meth.some((m) => ok.has(m))) {
      const what = COVER_TYPES[cover] || (TRUCK_TYPES[cover] || TRAILER_TYPES[cover] || { label: cover }).label;
      out.push([s.name, s.name + ' loads by ' + orList(meth.map((m) => LOADING_METHODS[m].toLowerCase())) + ' only; ' + what.toLowerCase() + " can't take that"]);
    }
  });
  return out;
}

// __SELF_UNLOAD__ (2026-10-08) Henry (customer booking portal spec): "self-unload required, done one
// of two distinct ways: (a) truck crane or (b) equipment on site that our driver operates." Kept on the
// job as billing_data.self_unload = 'crane' | 'onsite' (absent = none); booking form, dispatch app and
// the portal set it.
//   crane  - met by a unit with a crane: the Crane / knuckle boom or On-board crane / self-unload
//            feature, or the crane-truck type (per Henry: T07 today)
//   onsite - met by a DRIVER who can operate the customer's equipment: the per-driver setting
//            "Can self-unload (operate on-site equipment)" (rules.driver_caps[driverId], edited on every
//            driver in Drivers). Checked only when the driver is known.
// Not met -> 'self_unload_crane' / 'self_unload_operator' (warnings; Settings can make them Block).
// A job with this field set skips the older notes-based 'self_unload' cargo rule (the field is exact).
export const SELF_UNLOAD = { crane: 'Truck crane', onsite: 'On-site equipment (our driver operates it)' };
export const CRANE_FEATURES = ['crane', 'self_unload'];
export const CRANE_TYPES = ['crane_truck'];
const SU_ALIASES = { crane: 'crane', truckcrane: 'crane', onboardcrane: 'crane', boomtruck: 'crane',
  onsite: 'onsite', onsiteequipment: 'onsite', onsiteequipmentourdriveroperatesit: 'onsite',
  siteequipment: 'onsite', equipmentonsite: 'onsite', operator: 'onsite' };
export const DOWNTIME_KINDS = { maintenance: 'maintenance', out_of_service: 'out of service', time_off: 'time off', other: 'other' };
export const selfUnloadValue = (v) => SU_ALIASES[String(v || '').toLowerCase().replace(/[^a-z]/g, '')] || '';
export function selfUnloadOf(row) {
  const r = row || {};
  let v = r.self_unload || r.selfUnload;
  if (!v) {
    let bd = r.billing_data;
    if (typeof bd === 'string') { try { bd = JSON.parse(bd); } catch (e) { bd = null; } }
    if (bd && typeof bd === 'object') v = bd.self_unload;
  }
  return selfUnloadValue(v);
}
export const hasCrane = (cap) => {
  const c = cap || {};
  return CRANE_FEATURES.some((f) => (c.features || []).includes(f)) || CRANE_TYPES.includes(c.truckType);
};
// rules.driver_caps[driverId].can_self_unload (false when unknown)
export function driverCanSelfUnload(rules, driverId) {
  if (!driverId) return false;
  const caps = (asRules(rules).driver_caps || {})[String(driverId)] || {};
  return caps.can_self_unload === true;
}

export const typeInfo = (kind, type) => {
  const T = kind === 'trailer' ? TRAILER_TYPES : TRUCK_TYPES;
  return T[type] || { label: type ? String(type) : 'Unknown', cls: 'open' };
};

// Which fields a form shows for a class (the rest are hidden and saved as null).
export const classFields = (cls) => ({
  deck: cls === 'open' || cls === 'enclosed' || cls === 'pickup',
  height: cls === 'enclosed',
  deckHeight: cls === 'open',
  gcwr: cls === 'tractor',
  features: cls !== 'bulk',
});

// ---------------------------------------------------------------- rules
// Settings > Equipment rules (public.equipment_rules row 'main', data). Limits in
// feet / pounds. Each cargo rule: a job whose cargo type (loads.commodity - and its
// notes when match_notes) matches one of `cargo` should go on a unit whose type is in
// allowed_types or whose class is in allowed_classes (when either list is given) and
// that has at least one of needs_any_feature (when given). towed: the cargo is pulled,
// not carried (trailer moves).
//
// __EQUIP_V2__ (2026-10-04) Henry: "When cargo is being assigned over the limits there
// should be a warning. Sometimes we can negotiate those; overhang can be accommodated
// depending what it is." Every issue now has a SEVERITY:
//   info  - shown as a hint, never asks (missing data, near capacity)
//   warn  - the assign window lists it; "Assign anyway" is one tap, reason optional
//   block - "Assign anyway" needs a typed reason (only where Settings says so)
//   stop  - cannot be assigned at all (the driver or his unit no longer exists)
// Defaults: every limit / rule = warn; Settings > Equipment rules sets each code (and
// each cargo rule) to info / warn / block.
export const ISSUE_CODES = {
  no_unit: 'Driver has no unit',
  unit_inactive: 'Unit retired',
  unit_out_of_service: 'Unit in maintenance / out of service',
  unit_restricted: 'Unit under a temporary restriction (unavailable)',
  tractor_no_trailer: 'Tractor with no trailer',
  too_long: 'Longer than the deck (+ overhang allowance)',
  too_wide: 'Wider than a box inside',
  overhangs_sides: 'Hangs over the deck sides (within legal width)',
  over_legal_width: 'Over the legal width',
  too_tall: 'Taller than inside / a temporary height limit',
  over_legal_height: 'Travel height over the legal height',
  over_payload: 'Over the payload',
  season_over_payload: 'Over the half-load season limit',
  route_over_payload: 'Route: too much weight on board at once',
  route_deck_crowded: 'Route: jobs on board together may not fit',
  near_payload: 'Near the payload',
  missing_unit_data: 'Equipment data missing',
  missing_load_data: 'Job size / weight missing',
  over_company_day: 'Over the company daily limit',
  hos_driving: 'HOS: over the daily driving limit',
  hos_on_duty: 'HOS: over the daily on-duty limit',
  hos_window: 'HOS: over the elapsed-time window',
  hos_off: 'HOS: not enough time off before driving',
  timing_overlap: 'Overlaps the driver\'s other work',
  truck_required: 'Job needs a tarp / open deck truck',   // __TRUCK_REQUIRED__ __OPEN_DECK__
  self_unload_crane: 'Job needs a truck crane (self-unload)',   // __SELF_UNLOAD__
  self_unload_operator: 'Driver cannot run the site\'s equipment (self-unload)',
  site_loading: 'A stop\'s site loads only in a way this body / cover can\'t take',   // __SITE_LOADING__
  city_run: 'A stop in a city zone, on a unit that does no city runs',   // __OPERATING_LIMITS__
  beyond_radius: 'A stop farther from the unit\'s home point than its limit',
  limit_unchecked: 'Operating limits not checked (stop location / home point unknown)',
};
export const DEFAULT_SEVERITY = {
  near_payload: 'info', missing_unit_data: 'info', missing_load_data: 'info', limit_unchecked: 'info',
};
export const STOP_CODES = ['driver_missing', 'unit_missing'];
export const SEVERITIES = ['info', 'warn', 'block'];

export const DEFAULT_TIMING = {
  company_day_hours: 12,     // Henry: "they may not be able to run longer than 12 hrs"
  hos_driving_hours: 13,     // Canada federal commercial HOS, south of 60
  hos_on_duty_hours: 14,
  hos_window_hours: 16,
  hos_off_hours: 8,
};

export const DEFAULT_RULES = {
  version: 2,
  legal_width_ft: 8.5,
  legal_height_ft: 13.5,
  open_deck_overhang_ft: 3,
  near_capacity_pct: 90,
  severities: {},
  timing: { ...DEFAULT_TIMING },
  restrictions: [],
  half_load: { seasons: [], payload_pct: null, payload_cap_lb: null, note: '' },
  cargo_rules: [
    { id: 'machinery', name: 'Self-propelled machinery', severity: 'warn',
      cargo: ['Skid steer', 'Excavator', 'Mini excavator', 'Backhoe', 'Bulldozer', 'Wheel loader',
        'Telehandler', 'Manlift / boom lift', 'Manlift', 'Boom lift', 'Scissor lift', 'Forklift',
        'Compactor / packer', 'Packer', 'Roller', 'Trencher', 'Paver', 'Concrete buggy',
        'Farm tractor', 'Combine / harvester', '=Machinery'],
      allowed_classes: ['open'], needs_any_feature: ['ramps', 'tilt', 'beavertail'],
      note: 'Drives on: needs an open deck with ramps, a tilt deck or a beavertail.' },
    { id: 'vehicles', name: 'Vehicles', severity: 'warn',
      cargo: ['Car / pickup', 'Van', 'Bus', 'ATV / UTV', 'Motorcycle', 'Golf cart'],
      allowed_classes: ['open', 'enclosed'], needs_any_feature: ['ramps', 'tilt', 'beavertail', 'liftgate'],
      note: 'Needs ramps, a tilt deck, a beavertail or a liftgate.' },
    { id: 'non_running', name: 'Non-running vehicle', severity: 'warn',
      cargo: ['Truck (non-running)', 'Non-running'],
      allowed_classes: ['open'], needs_any_feature: ['winch'],
      note: 'Does not drive: needs a winch.' },
    { id: 'trailer_move', name: 'Trailer move (towed)', severity: 'warn', towed: true,
      cargo: ['Trailer move'],
      allowed_classes: ['tractor'],
      note: 'The trailer is pulled: send a tractor or hotshot with no trailer of its own.' },
    { id: 'aggregates', name: 'Aggregates & bulk', severity: 'warn',
      cargo: ['Gravel', 'Sand', 'Topsoil', 'Fill / dirt', 'Stone / rip-rap', 'Asphalt',
        'Mulch / wood chips', 'Salt', 'Grain'],
      allowed_types: ['dump_truck', 'dump', 'hopper'],
      note: 'Loose material: needs a dump truck, dump trailer or hopper.' },
    { id: 'containers', name: 'Containers & structures', severity: 'warn',
      cargo: ['Shipping container', 'Portable office / trailer', 'Shed / outbuilding', 'Modular section', 'Storage tank'],
      allowed_classes: ['open'], needs_any_feature: ['tilt', 'crane'],
      note: 'Usually loaded by tilting or crane.' },
    { id: 'crane_loaded', name: 'Crane-loaded material', severity: 'warn',
      cargo: ['Structural steel / beams', 'Precast concrete', 'Roof trusses'],
      needs_any_feature: ['top_loading', 'crane'],
      note: 'Usually lifted on from above: needs an open deck or a top-loading body.' },
    { id: 'self_unload', name: 'Self-unload (no forklift at delivery)', severity: 'warn', match_notes: true,
      cargo: ['Self-unload', 'Self unload', 'No forklift', 'Crane off', 'Unload with crane'],
      needs_any_feature: ['self_unload', 'crane'],
      note: 'Nobody to unload it: needs an on-board crane.' },
  ],
};

const num = (v) => {
  if (v === null || v === undefined || v === '' || typeof v === 'boolean') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const pos = (v) => { const n = num(v); return n !== null && n > 0 ? n : null; };
const listOf = (v) => (Array.isArray(v) ? v.map((x) => String(x || '').trim()).filter(Boolean) : []);
const isDay = (s) => /^\d{4}-\d{2}-\d{2}$/.test(String(s || ''));
const sev = (v, dflt) => (SEVERITIES.includes(v) ? v : dflt);

export const RESTRICTION_FIELDS = {
  // global (every unit)
  legal_width_ft: { scope: 'global', label: 'Legal width (ft)' },
  legal_height_ft: { scope: 'global', label: 'Legal height (ft)' },
  open_deck_overhang_ft: { scope: 'global', label: 'Open-deck overhang (ft)' },
  max_payload_lb: { scope: 'global', label: 'Most weight on any unit (lb)' },
  // one truck / trailer
  payload_lb: { scope: 'unit', label: 'Payload (lb)' },
  deck_length_ft: { scope: 'unit', label: 'Deck / inside length (ft)' },
  deck_width_ft: { scope: 'unit', label: 'Deck / inside width (ft)' },
  max_cargo_height_ft: { scope: 'unit', label: 'Cargo height (ft)' },
  unavailable: { scope: 'unit', label: 'Unavailable (out of area, ...)' },
};

export function normalizeRules(raw) {
  const r = (raw && typeof raw === 'object') ? raw : {};
  const d = DEFAULT_RULES;
  const ov = num(r.open_deck_overhang_ft);
  const out = {
    version: 2,
    legal_width_ft: pos(r.legal_width_ft) ?? d.legal_width_ft,
    legal_height_ft: pos(r.legal_height_ft) ?? d.legal_height_ft,
    open_deck_overhang_ft: ov !== null && ov >= 0 ? ov : d.open_deck_overhang_ft,
    near_capacity_pct: pos(r.near_capacity_pct) ?? d.near_capacity_pct,
    severities: {},
    timing: {},
    restrictions: [],
    half_load: { seasons: [], payload_pct: null, payload_cap_lb: null, note: '' },
    cargo_rules: [],
    driver_caps: {},
    loading_matrix: {},
    sites: [],
    places: [],
    telematics_units: {},
    telematics_readings: {},
  };
  // __SITE_LOADING__ body / cover -> loading methods it takes (Settings table), and the sites with a loading method set
  // (mirrored from the hub address book and portal places into this row by the hub)
  const lm = (r.loading_matrix && typeof r.loading_matrix === 'object' && !Array.isArray(r.loading_matrix)) ? r.loading_matrix : DEFAULT_LOADING_MATRIX;
  Object.keys(lm).forEach((k0) => {
    const k = String(k0 || '').trim();
    if (/^[a-z0-9_]{2,40}$/.test(k)) out.loading_matrix[k] = loadingMethods(lm[k0]);
  });
  out.sites = cleanSites(r.sites);
  out.places = cleanPlaces(r.places);   // __OPERATING_LIMITS__ address-book names for home points
  out.telematics_units = cleanTelematicsUnits(r.telematics_units);   // __TELEMATICS__ board truck form
  out.telematics_readings = cleanTelematicsReadings(r.telematics_readings);   // __TELEMATICS__ hub mirror
  // __OPERATING_LIMITS__ city zones (presets until edited) and each truck's limits
  out.city_zones = Array.isArray(r.city_zones) ? cleanZones(r.city_zones) : CITY_PRESETS.map((z) => ({ ...z, polygon: [] }));
  out.unit_limits = cleanUnitLimits(r.unit_limits);
  // __SELF_UNLOAD__ per-driver abilities, by drivers.id (no column needed)
  const dc = (r.driver_caps && typeof r.driver_caps === 'object' && !Array.isArray(r.driver_caps)) ? r.driver_caps : {};
  Object.keys(dc).forEach((k) => {
    const v = dc[k];
    if (k && v && typeof v === 'object' && typeof v.can_self_unload === 'boolean') out.driver_caps[String(k)] = { can_self_unload: v.can_self_unload };
  });
  const sv = (r.severities && typeof r.severities === 'object') ? r.severities : {};
  Object.keys(ISSUE_CODES).forEach((k) => { if (SEVERITIES.includes(sv[k])) out.severities[k] = sv[k]; });
  const tm = (r.timing && typeof r.timing === 'object') ? r.timing : {};
  Object.keys(DEFAULT_TIMING).forEach((k) => { out.timing[k] = pos(tm[k]) ?? DEFAULT_TIMING[k]; });
  (Array.isArray(r.restrictions) ? r.restrictions : []).forEach((x, i) => {
    if (!x || typeof x !== 'object' || !RESTRICTION_FIELDS[x.field] || !isDay(x.start)) return;
    const f = RESTRICTION_FIELDS[x.field];
    const value = x.field === 'unavailable' ? true : pos(x.value);
    if (value === null) return;
    if (f.scope === 'unit' && !(x.unit_id && (x.unit_kind === 'truck' || x.unit_kind === 'trailer'))) return;
    const item = {
      id: String(x.id || 'r' + (i + 1)), field: x.field, value, start: x.start,
      end: isDay(x.end) ? x.end : null, note: String(x.note || '').trim(),
      unit_kind: f.scope === 'unit' ? x.unit_kind : null, unit_id: f.scope === 'unit' ? String(x.unit_id) : null,
    };
    // __SELF_UNLOAD__ / __UNIT_DOWNTIME__ (2026-10-08) a dated downtime says why (maintenance, ...)
    if (x.field === 'unavailable' && Object.prototype.hasOwnProperty.call(DOWNTIME_KINDS, x.kind)) item.kind = x.kind;
    out.restrictions.push(item);
  });
  const hl = (r.half_load && typeof r.half_load === 'object') ? r.half_load : {};
  (Array.isArray(hl.seasons) ? hl.seasons : []).forEach((s) => {
    if (s && isDay(s.start) && isDay(s.end) && s.start <= s.end) out.half_load.seasons.push({ start: s.start, end: s.end });
  });
  const pct = pos(hl.payload_pct);
  out.half_load.payload_pct = pct !== null && pct <= 100 ? pct : null;
  out.half_load.payload_cap_lb = pos(hl.payload_cap_lb);
  out.half_load.note = String(hl.note || '').trim();
  const src = Array.isArray(r.cargo_rules) ? r.cargo_rules : d.cargo_rules;
  src.forEach((x, i) => {
    if (!x || typeof x !== 'object') return;
    const cargo = listOf(x.cargo);
    if (!cargo.length) return;
    out.cargo_rules.push({
      id: String(x.id || 'rule' + (i + 1)),
      name: String(x.name || 'Rule ' + (i + 1)).trim(),
      severity: sev(x.severity, 'warn'),
      towed: x.towed === true,
      match_notes: x.match_notes === true,
      cargo,
      allowed_types: listOf(x.allowed_types),
      allowed_classes: listOf(x.allowed_classes),
      needs_any_feature: listOf(x.needs_any_feature),
      note: String(x.note || ''),
    });
  });
  return out;
}
const asRules = (rules) => (rules && rules.version === 2 && Array.isArray(rules.cargo_rules) && rules.timing
  && rules.driver_caps && typeof rules.driver_caps === 'object' && rules.loading_matrix && typeof rules.loading_matrix === 'object'
  ? rules : normalizeRules(rules));

// Cargo text match: case, spaces and punctuation ignored ("Skidsteer" = "Skid steer",
// "Manlift" is found in "Manlift / boom lift"). A blank cargo type matches nothing.
// An entry starting with "=" matches only exactly ("=Machinery" is not "Machinery parts").
export const cargoKey = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
export function cargoMatches(commodity, entry) {
  const c = cargoKey(commodity); const e = cargoKey(entry);
  if (!c || !e) return false;
  if (String(entry).trim().startsWith('=')) return c === e;
  if (c === e) return true;
  if (e.length >= 4 && c.includes(e)) return true;
  if (c.length >= 5 && e.includes(c)) return true;
  return false;
}
// The first rule whose cargo list matches the job's cargo type (or, for a match_notes
// rule, a phrase in its notes - "no forklift", "self-unload").
export function matchCargoRule(commodity, rules, notes) {
  const R = asRules(rules);
  for (const r of R.cargo_rules) {
    if (r.cargo.some((e) => cargoMatches(commodity, e))) return r;
    if (r.match_notes && notes && r.cargo.some((e) => !String(e).trim().startsWith('=') && cargoKey(e).length >= 4 && cargoKey(notes).includes(cargoKey(e)))) return r;
  }
  return null;
}

// ---------------------------------------------------------------- dates
export const todayISO = () => {
  const d = new Date();
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
};
const inRange = (day, start, end) => !!day && day >= start && (!end || day <= end);
// Global limits in effect on `day` (temporary restrictions replace the normal value).
export function rulesOn(rules, day) {
  const R = asRules(rules);
  const D = isDay(day) ? day : todayISO();
  const g = { legal_width_ft: R.legal_width_ft, legal_height_ft: R.legal_height_ft, open_deck_overhang_ft: R.open_deck_overhang_ft, max_payload_lb: null, notes: [] };
  R.restrictions.forEach((x) => {
    if (RESTRICTION_FIELDS[x.field].scope !== 'global' || !inRange(D, x.start, x.end)) return;
    g[x.field] = x.value;
    g.notes.push(RESTRICTION_FIELDS[x.field].label + ' ' + x.value + ' (temporary, ' + x.start + (x.end ? ' to ' + x.end : ' on') + (x.note ? ': ' + x.note : '') + ')');
  });
  return g;
}
// The half-load season `day` falls in, or null. __EQUIP_R5__ the dates alone make a season: each
// unit's own half-load caps apply inside it; the fleet-wide % / lb cap (optional) covers units
// without their own caps.
export function halfLoadOn(rules, day) {
  const R = asRules(rules);
  const D = isDay(day) ? day : todayISO();
  const s = R.half_load.seasons.find((x) => inRange(D, x.start, x.end));
  if (!s) return null;
  return { ...s, payload_pct: R.half_load.payload_pct, payload_cap_lb: R.half_load.payload_cap_lb, note: R.half_load.note };
}

// __EQUIP_R5__ (2026-10-05) a truck's / trailer's own half-load caps (Henry: "half-load affects
// each unit differently (per-axle and total vehicle caps)"). Columns half_load_total_kg and
// half_load_axle_caps ([{ axle, cap_kg }]) are GROSS limits (the unit with its load). The unit may
// weigh the smaller of the total cap and the sum of its axle caps; cargo allowed = that - tare
// (tare unknown: the cap is compared with the cargo alone). Blank = no caps of its own.
export function halfLoadSpec(row) {
  if (!row) return null;
  const total = kgToLb(row.half_load_total_kg);
  const axles = (Array.isArray(row.half_load_axle_caps) ? row.half_load_axle_caps : [])
    .filter((a) => a && typeof a === 'object')
    .map((a) => ({ axle: String(a.axle || '').trim() || 'axle', lb: kgToLb(a.cap_kg) }))
    .filter((a) => a.lb !== null);
  if (total === null && !axles.length) return null;
  const sum = axles.length ? axles.reduce((t, a) => t + a.lb, 0) : null;
  const gross = total !== null && sum !== null ? Math.min(total, sum) : (total !== null ? total : sum);
  return { grossLb: gross, totalLb: total, axles, axleSumLb: sum, tareLb: kgToLb(row.tare_weight_kg) };
}

// ---------------------------------------------------------------- unit
// Which unit does this driver run on `day` (the job date)?
//   1. a truck picked WITH the assignment (Quick Assign / Edit / AI suggestion / the gate's
//      unit picker): that truck's combination with this driver, else its current combination,
//      else the truck alone (loads.truck_id left over from a previous driver is NOT used);
//   2. __EQUIP_ASSIGN__ his rows in driver_equipment_assignments (fleet.assignments) in effect
//      on `day` (start_date / end_date, blank = open): the PRIMARY power unit (with the trailer
//      coupled to it) when it is in service; if it is in maintenance / out of service /
//      retired, an in-service shared / spare one (source 'spare_truck'); with no primary, the
//      shared one - and when several could be meant, `choices` lists them so the dispatcher
//      picks (the gate shows a unit picker). Rows exist but none on that day -> no unit.
//   3. (a driver with no rows) the combination whose default driver he is - UNLESS its truck
//      or trailer is in maintenance / out of service / retired and drivers.current_truck_id
//      names another truck that is in service: then that one (spare);
//   4. drivers.current_truck_id: its current combination, else the truck alone.
// A unit pointing at a truck / trailer that no longer exists comes back with `missing`.
// fleet = { drivers, trucks, trailers, combos, assignments } (rows as stored).
export function resolveUnit({ driverId, truckId, fleet, day }) {
  const F = fleet || {};
  const trucks = F.trucks || []; const trailers = F.trailers || []; const combos = F.combos || [];
  const truckById = (id) => trucks.find((t) => t.id === id) || null;
  const trailerById = (id) => trailers.find((t) => t.id === id) || null;
  const fromCombo = (c, source) => {
    const truck = truckById(c.truck_id);
    if (!truck) return { kind: 'missing', source, combo: c, truck: null, trailer: null, missing: 'truck' };
    const trailer = c.trailer_id ? trailerById(c.trailer_id) : null;
    if (c.trailer_id && !trailer) return { kind: 'missing', source, combo: c, truck, trailer: null, missing: 'trailer' };
    return { kind: c.trailer_id ? 'combination' : 'truck', source, combo: c, truck, trailer };
  };
  const fromTruck = (tid, source) => {
    const truck = truckById(tid);
    if (!truck) return { kind: 'missing', source, combo: null, truck: null, trailer: null, missing: 'truck' };
    const mine = combos.find((c) => c.truck_id === tid && driverId && c.default_driver_id === driverId);
    const def = mine || combos.find((c) => c.truck_id === tid && c.is_default);
    if (def) return fromCombo(def, source);
    return { kind: 'truck', source, combo: null, truck, trailer: null };
  };
  if (truckId) return fromTruck(truckId, 'job_truck');
  const down = (r) => !!r && (r.is_active === false || ['maintenance', 'out_of_service'].includes(String(r.status || '').toLowerCase()));
  const mineRows = driverId ? (F.assignments || []).filter((a) => a && a.driver_id === driverId) : [];
  if (mineRows.length) {
    const D = isDay(day) ? day : todayISO();
    const num = (u) => String((u.truck && u.truck.truck_number) || '');
    const units = mineRows
      .filter((a) => (!a.start_date || a.start_date <= D) && (!a.end_date || D <= a.end_date))
      .map((a) => ({ a, u: fromTruck(a.truck_id, a.is_primary ? 'primary' : 'shared') }))
      .sort((x, y) => (y.a.is_primary ? 1 : 0) - (x.a.is_primary ? 1 : 0) || num(x.u).localeCompare(num(y.u)));
    if (!units.length) return null;
    const fine = (x) => !x.u.missing && !down(x.u.truck) && !down(x.u.trailer);
    const choicesOf = (list) => list.map((x) => ({ truckId: x.a.truck_id, label: unitLabel(x.u), primary: !!x.a.is_primary }));
    const prim = units.find((x) => x.a.is_primary);
    const others = units.filter((x) => !x.a.is_primary);
    if (prim && fine(prim)) return prim.u;
    if (prim) {
      const spares = others.filter(fine);
      if (!spares.length) return prim.u;
      const out = { ...spares[0].u, source: 'spare_truck', ownUnit: prim.u };
      if (spares.length > 1) out.choices = choicesOf(spares);
      return out;
    }
    const ok = others.filter(fine);
    const pool = ok.length ? ok : others;
    const out = { ...pool[0].u };
    if (pool.length > 1) out.choices = choicesOf(pool);
    return out;
  }
  if (driverId) {
    const c = combos.find((x) => x.default_driver_id === driverId);
    const d = (F.drivers || []).find((x) => x.id === driverId);
    if (c) {
      const u = fromCombo(c, 'default_driver');
      const spare = d && d.current_truck_id && d.current_truck_id !== c.truck_id ? truckById(d.current_truck_id) : null;
      if (!u.missing && (down(u.truck) || down(u.trailer)) && spare && !down(spare)) {
        const s = fromTruck(spare.id, 'spare_truck');
        if (!s.missing) return { ...s, ownCombo: c };
      }
      return u;
    }
    if (d && d.current_truck_id) return fromTruck(d.current_truck_id, 'current_truck');
  }
  return null;
}

export const unitLabel = (unit) => {
  if (!unit) return 'no unit';
  if (unit.combo && unit.combo.combination_name) return unit.combo.combination_name;
  const t = unit.truck ? (unit.truck.truck_number || 'truck') : '';
  return unit.trailer ? t + ' + ' + (unit.trailer.trailer_number || 'trailer') : t;
};

// ---------------------------------------------------------------- pairing (hitch) rules
// __EQUIP_ASSIGN__ / __EQUIP_R5__ which trailer a power unit can pull (Equipment Assignment view).
// Twin: equipment_compat.py (coupling_check); shared coupling_cases in the cases file.
const hitchTruckName = (t) => (t ? t.truck_number || 'truck' : 'truck');
const hitchTrailerName = (t) => (t ? t.trailer_number || 'trailer' : 'trailer');
export const HITCH_LABEL = { fifth_wheel: 'a fifth wheel', gooseneck: 'a gooseneck hitch', bumper: 'a bumper / pintle hitch' };
const BUMPER_TRAILERS = ['utility', 'tilt', 'car_hauler', 'bumper_pull'];
const STRAIGHT_CLASSES = ['open', 'enclosed', 'bulk'];

// what the trailer couples with: gooseneck trailers a gooseneck ball; bumper-pull deck, utility, tilt /
// car trailers a bumper hitch or pintle; the rest (flatbed, step deck, lowboy, van, tanker,
// dump ...) a fifth wheel. A trailer marked "Bumper / pintle hitch" is bumper-pull.
// __EQUIP_R6__ every coupler the trailer has: a convertible trailer (fifth-wheel kingpin + gooseneck
// coupler) hitches to either a gooseneck or a fifth-wheel tow unit.
export function trailerHitches(trailer) {
  const f = listOf(trailer && trailer.features);
  const main = trailerHitch(trailer);
  if (main !== 'bumper' && f.includes('convertible_coupler')) return ['gooseneck', 'fifth_wheel'];
  return [main];
}
export function trailerHitch(trailer) {
  const f = listOf(trailer && trailer.features);
  const type = trailer && trailer.trailer_type;
  if (f.includes('bumper_hitch')) return 'bumper';
  if (type === 'gooseneck') return 'gooseneck';
  if (BUMPER_TRAILERS.includes(type)) return 'bumper';
  return 'fifth_wheel';
}
// what the power unit can pull: its hitch features, plus what its type always has (a tractor a
// fifth wheel, a hotshot a gooseneck ball and a receiver, a pickup a receiver). A fifth wheel
// with "Gooseneck adapter" also takes a gooseneck.
export function truckHitches(truck) {
  const f = listOf(truck && truck.features);
  const type = truck && truck.truck_type;
  const h = new Set();
  if (f.includes('fifth_wheel') || type === 'day_cab' || type === 'sleeper') h.add('fifth_wheel');
  if (f.includes('gooseneck_hitch') || type === 'hotshot') h.add('gooseneck');
  if (h.has('fifth_wheel') && f.includes('gooseneck_adapter')) h.add('gooseneck');
  if (f.includes('bumper_hitch') || type === 'hotshot' || type === 'pickup') h.add('bumper');
  return h;
}
export const isStraightTruck = (truck) => {
  const info = TRUCK_TYPES[truck && truck.truck_type];
  return !!info && STRAIGHT_CLASSES.includes(info.cls);
};
const downText = (row) => {
  if (!row) return null;
  if (row.is_active === false) return 'retired';
  const s = String(row.status || '').toLowerCase();
  if (s === 'maintenance') return 'in maintenance';
  if (s === 'out_of_service') return 'out of service';
  return null;
};
export const unitDown = (row) => downText(row);

// -> { ok, reason } ; warn = a unit in the shop (allowed, said so)
export function couplingCheck(truck, trailer) {
  if (!truck || !trailer) return { ok: false, reason: 'Pick a power unit and a trailer.' };
  const t = hitchTruckName(truck); const p = hitchTrailerName(trailer);
  const have = truckHitches(truck);
  const needs = trailerHitches(trailer);
  const need = needs[0];
  if (isStraightTruck(truck) && !have.size) {
    return { ok: false, reason: t + ' is a straight truck (' + typeInfo('truck', truck.truck_type).label.toLowerCase() + ') with no hitch - it pulls no trailer. (If it has one, tick its hitch in Trucks.)' };
  }
  if (!needs.some((n) => have.has(n))) {
    const has = have.size ? [...have].map((x) => HITCH_LABEL[x]).join(' and ') : 'no hitch';
    const why = needs.length > 1
      ? p + ' is convertible (fifth-wheel kingpin + gooseneck coupler) - ' + t + ' has ' + has + ' (a fifth wheel or a gooseneck hitch is needed).'
      : need === 'gooseneck'
      ? p + ' is a gooseneck trailer - ' + t + ' has ' + has + ' (a gooseneck hitch, or a fifth wheel with a gooseneck adapter, is needed).'
      : need === 'fifth_wheel'
        ? p + ' (' + typeInfo('trailer', trailer.trailer_type).label.toLowerCase() + ') needs a fifth wheel - ' + t + ' has ' + has + '.'
        : p + ' is a bumper-pull trailer - ' + t + ' has ' + has + ' (a bumper / pintle hitch is needed).';
    return { ok: false, reason: why };
  }
  const warns = [[t, downText(truck)], [p, downText(trailer)]].filter((x) => x[1]).map((x) => x[0] + ' is ' + x[1]);
  return { ok: true, warn: warns.length ? warns.join('; ') : null };
}


const kgToLb = (kg) => { const n = pos(kg); return n === null ? null : n * LB_PER_KG; };
const cmToFt = (cm) => { const n = pos(cm); return n === null ? null : n * FT_PER_CM; };
const ownPayloadLb = (row) => {
  if (!row) return null;
  const p = kgToLb(row.payload_kg);
  if (p !== null) return { lb: p, from: 'payload' };
  const g = pos(row.max_weight_kg); const t = pos(row.tare_weight_kg);
  if (g !== null && t !== null && g > t) return { lb: (g - t) * LB_PER_KG, from: 'GVWR - tare' };
  return null;
};
const inactiveOf = (row) => row && (row.is_active === false);
const shopOf = (row) => row && ['maintenance', 'out_of_service'].includes(String(row.status || '').toLowerCase());

// Capacity of a unit on `day`, in feet / pounds.
//   deck  : the trailer's when there is one, else the truck's
//   payload (what may be loaded on it):
//     truck alone       : payload_kg, else GVWR (max_weight_kg) - tare
//     truck + trailer   : the SMALLEST known of the trailer's payload (payload_kg, else its
//                         GVWR - tare), the tractor's GCWR - (both tares), the combination's
//                         manual cap (combined_max_weight_kg)
//   temporary restrictions in effect on `day` replace payload / deck length / width / cargo
//   height (a truck's or its trailer's), or make the unit unavailable; a global "most weight
//   on any unit" caps the payload; the half-load season adds seasonPayloadLb.
//   features: truck's + trailer's (+ an open deck always loads from above and the side)
export function unitCapacity(unit, rules, day) {
  if (!unit || !unit.truck) return null;
  const R = asRules(rules);
  const D = isDay(day) ? day : todayISO();
  const { truck, trailer, combo } = unit;
  const tInfo = typeInfo('truck', truck.truck_type);
  const rInfo = trailer ? typeInfo('trailer', trailer.trailer_type) : null;
  const carrier = trailer || truck;
  const cls = rInfo ? rInfo.cls : tInfo.cls;
  const type = trailer ? trailer.trailer_type : truck.truck_type;
  const cap = {
    cls, type, typeLabel: (rInfo || tInfo).label, label: unitLabel(unit),
    tractorOnly: !trailer && tInfo.cls === 'tractor', truckType: truck.truck_type,
    truckId: truck.id,   // __OPERATING_LIMITS__ the limits belong to the power unit
    deckLengthFt: cmToFt(carrier.cargo_length_cm), deckWidthFt: cmToFt(carrier.cargo_width_cm),
    insideHeightFt: cls === 'enclosed' ? cmToFt(carrier.cargo_height_cm) : null,
    deckHeightFt: cls === 'open' ? cmToFt(carrier.deck_height_cm) : null,
    maxCargoHeightFt: null,
    payloadLb: null, payloadFrom: null, seasonPayloadLb: null, seasonFrom: null, season: null,
    features: Array.from(new Set([...(listOf(truck.features)), ...(trailer ? listOf(trailer.features) : []),
      ...(cls === 'open' ? ['top_loading', 'side_loading'] : [])])),
    out: [], restricted: [], notes: [],
    // __TRUCK_REQUIRED__ the load-carrying unit's own features (a tractor's do not make a trailer closed)
    carrierFeatures: listOf(carrier.features),
  };
  if (inactiveOf(truck)) cap.out.push({ code: 'unit_inactive', what: 'Truck ' + (truck.truck_number || ''), why: 'retired (inactive)' });
  if (shopOf(truck)) cap.out.push({ code: 'unit_out_of_service', what: 'Truck ' + (truck.truck_number || ''), why: String(truck.status).replace(/_/g, ' ') });
  if (trailer && inactiveOf(trailer)) cap.out.push({ code: 'unit_inactive', what: 'Trailer ' + (trailer.trailer_number || ''), why: 'retired (inactive)' });
  if (trailer && shopOf(trailer)) cap.out.push({ code: 'unit_out_of_service', what: 'Trailer ' + (trailer.trailer_number || ''), why: String(trailer.status).replace(/_/g, ' ') });

  const cands = [];
  if (trailer) {
    const tp = ownPayloadLb(trailer);
    if (tp) cands.push({ lb: tp.lb, from: 'trailer ' + tp.from });
    const gcwr = kgToLb(truck.gcwr_kg);
    const tares = [pos(truck.tare_weight_kg), pos(trailer.tare_weight_kg)];
    if (gcwr !== null && tares.every((x) => x !== null)) cands.push({ lb: gcwr - (tares[0] + tares[1]) * LB_PER_KG, from: 'GCWR - tares' });
  } else if (tInfo.cls !== 'tractor') {
    const op = ownPayloadLb(truck);
    if (op) cands.push({ lb: op.lb, from: op.from });
  }
  const manual = kgToLb(combo && combo.combined_max_weight_kg);
  if (manual !== null) cands.push({ lb: manual, from: 'combination cap' });
  if (cands.length) {
    const best = cands.reduce((a, b) => (b.lb < a.lb ? b : a));
    cap.payloadLb = Math.max(0, best.lb); cap.payloadFrom = best.from;
  }
  // temporary restrictions on this truck / its trailer
  const mine = R.restrictions.filter((x) => RESTRICTION_FIELDS[x.field].scope === 'unit' && inRange(D, x.start, x.end)
    && ((x.unit_kind === 'truck' && x.unit_id === truck.id) || (trailer && x.unit_kind === 'trailer' && x.unit_id === trailer.id)));
  mine.forEach((x) => {
    const when = ' (temporary, ' + x.start + (x.end ? ' to ' + x.end : ' on') + (x.note ? ': ' + x.note : '') + ')';
    const who = x.unit_kind === 'truck' ? (truck.truck_number || 'truck') : (trailer.trailer_number || 'trailer');
    if (x.field === 'unavailable') { cap.restricted.push(who + ' is unavailable' + (x.kind ? ' (' + DOWNTIME_KINDS[x.kind] + ')' : '') + when); return; }
    if (x.field === 'payload_lb') { cap.payloadLb = x.value; cap.payloadFrom = 'temporary limit'; }
    if (x.field === 'deck_length_ft') cap.deckLengthFt = x.value;
    if (x.field === 'deck_width_ft') cap.deckWidthFt = x.value;
    if (x.field === 'max_cargo_height_ft') cap.maxCargoHeightFt = x.value;
    cap.notes.push(who + ': ' + RESTRICTION_FIELDS[x.field].label + ' ' + x.value + when);
  });
  const g = rulesOn(R, D);
  if (g.max_payload_lb !== null && (cap.payloadLb === null || g.max_payload_lb < cap.payloadLb)) { cap.payloadLb = g.max_payload_lb; cap.payloadFrom = 'temporary limit for every unit'; }
  const season = halfLoadOn(R, D);
  if (season) {
    // __EQUIP_R5__ each unit's own caps; the fleet-wide rule only where a unit has none
    const lims = [];
    const T = halfLoadSpec(truck); const P = trailer ? halfLoadSpec(trailer) : null;
    const allow = (h) => Math.max(0, h.grossLb - (h.tareLb || 0));
    if (!trailer) {
      if (T && tInfo.cls !== 'tractor') lims.push({ lb: allow(T), from: { kind: 'unit', who: truck.truck_number || 'truck', grossLb: T.grossLb, tareLb: T.tareLb } });
    } else {
      if (P) lims.push({ lb: allow(P), from: { kind: 'unit', who: trailer.trailer_number || 'trailer', grossLb: P.grossLb, tareLb: P.tareLb } });
      if (T && P) {
        const tare = (T.tareLb || 0) + (P.tareLb || 0);
        lims.push({ lb: Math.max(0, T.grossLb + P.grossLb - tare),
          from: { kind: 'combined', who: unitLabel(unit) + ' together', grossLb: T.grossLb + P.grossLb, tareLb: T.tareLb !== null && P.tareLb !== null ? tare : null } });
      }
    }
    const fleetToo = trailer ? (!T || !P) : !T;
    if (fleetToo) {
      const opts = [];
      if (season.payload_pct !== null && cap.payloadLb !== null) opts.push(cap.payloadLb * season.payload_pct / 100);
      if (season.payload_cap_lb !== null) opts.push(season.payload_cap_lb);
      if (opts.length) lims.push({ lb: Math.min(...opts), from: { kind: 'fleet' } });
    }
    if (lims.length) {
      const best = lims.reduce((a, b) => (b.lb < a.lb ? b : a));
      cap.seasonPayloadLb = best.lb; cap.seasonFrom = best.from; cap.season = season;
    }
  }
  return cap;
}

// A job row -> feet / pounds (the columns are misnamed, see the header).
export function loadDims(row) {
  const r = row || {};
  return {
    id: r.id || null,
    label: r.load_number ? '#' + r.load_number : (r.id ? '#' + String(r.id).slice(0, 8) : 'this job'),
    weightLb: pos(r.weight_lb !== undefined ? r.weight_lb : r.weight_kg),
    lengthFt: pos(r.length_ft !== undefined ? r.length_ft : r.length_cm),
    widthFt: pos(r.width_ft !== undefined ? r.width_ft : r.width_cm),
    heightFt: pos(r.height_ft !== undefined ? r.height_ft : r.height_cm),
    commodity: String(r.commodity || '').trim(),
    notes: String(r.notes || ''),
    truckRequired: truckRequiredOf(r),   // __TRUCK_REQUIRED__
    selfUnload: selfUnloadOf(r),         // __SELF_UNLOAD__
    row: r,                              // __SITE_LOADING__ its stops
  };
}

// ---------------------------------------------------------------- formatting
const fmtNum = (n, d) => {
  const f = Number(n).toFixed(d);
  const [a, b] = f.split('.');
  const s = a.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return b && Number(b) !== 0 ? s + '.' + b : s;
};
export const fmtFt = (ft, units) => (ft === null || ft === undefined ? '?' : units === 'metric'
  ? fmtNum(ft * 0.3048, 2) + ' m' : fmtNum(ft, 1) + ' ft');
export const fmtLb = (lb, units) => (lb === null || lb === undefined ? '?' : units === 'metric'
  ? fmtNum(lb / LB_PER_KG, 0) + ' kg' : fmtNum(lb, 0) + ' lb');
export const fmtH = (min) => fmtNum(Math.round(min / 6) / 10, 1) + ' h';

const featureLabel = (f) => (EQUIPMENT_FEATURES[f] || f).toLowerCase();
const orList = (a) => (a.length <= 1 ? (a[0] || '') : a.slice(0, -1).join(', ') + ' or ' + a[a.length - 1]);
const articled = (s) => (/^[aeiou]/i.test(s) ? 'an ' : 'a ') + s.toLowerCase();

// ---------------------------------------------------------------- the check
// checkCompatibility({ unit, loads, stops, rules, units, driverName, date, driverMissing, timing })
//   unit  : resolveUnit(...) or null
//   loads : job rows (loads.*) - one job, or every job of a route
//   stops : optional driving order [{ load_id, stop_type }] (route); without it the
//           jobs are counted as all on board together (the safe assumption)
//   date  : the job / route day (YYYY-MM-DD) - temporary restrictions and the half-load
//           season in effect that day apply
//   timing: optional { existing, added, prevDayEnd } - see timingIssues
// -> { ok, canAssign, needsReason, stops, blocking, warnings, infos, issues, unit, peakLb, peakNote, timingNote, season }
//   ok          : nothing above info (it fits) - no window needed
//   canAssign   : no stop issue
//   needsReason : a block issue - "Assign anyway" needs a typed reason
//   driverId: __SELF_UNLOAD__ whose abilities count for on-site self-unload (absent = not checked)
export function checkCompatibility({ unit, loads, stops, rules, units, driverName, date, driverMissing, timing, driverId } = {}) {
  const R = asRules(rules);
  const U = units === 'metric' ? 'metric' : 'imperial';
  const D = isDay(date) ? date : todayISO();
  const G = rulesOn(R, D);
  const jobs = (loads || []).map(loadDims);
  const issues = [];
  const add = (code, message, job, sevOverride) => issues.push({
    severity: STOP_CODES.includes(code) ? 'stop' : (sevOverride || R.severities[code] || DEFAULT_SEVERITY[code] || 'warn'),
    code, message, load_id: job ? job.id : null,
  });
  const who = driverName ? driverName + ' has' : 'This driver has';
  if (driverMissing) {
    add('driver_missing', 'This driver no longer exists on the board - pick another driver.');
    return finish(issues, null, null, null, null, null);
  }
  if (unit && unit.missing) {
    add('unit_missing', 'The ' + unitLabel(unit) + ' ' + unit.missing + ' no longer exists - fix the unit in Combinations / Drivers > Equipment first.');
    return finish(issues, null, null, null, null, null);
  }
  let timingNote = null;
  const tm = timing ? timingIssues({ ...timing, settings: R.timing, driverName }) : null;
  if (!unit || !unit.truck) {
    add('no_unit', who + ' no truck or truck-trailer combination assigned, so nothing about the equipment can be checked. Set it in Drivers > Equipment (or Combinations > Default driver).');
    if (tm) { tm.issues.forEach((x) => add(x.code, x.message)); timingNote = tm.note; }
    return finish(issues, null, null, null, timingNote, halfLoadOn(R, D));
  }
  const cap = unitCapacity(unit, R, D);
  cap.out.forEach((o) => add(o.code, o.what + ' is ' + o.why + '.'));
  cap.restricted.forEach((m) => add('unit_restricted', m + '.'));
  G.notes.forEach((m) => cap.notes.push(m));

  // __SELF_UNLOAD__ an explicit self-unload way replaces the notes-based self-unload cargo rule
  const ruleOf = jobs.map((j) => { const r = matchCargoRule(j.commodity, R, j.notes); return r && r.id === 'self_unload' && j.selfUnload ? null : r; });
  const allTowed = jobs.length > 0 && ruleOf.every((r) => r && r.towed);
  if (cap.tractorOnly && !allTowed) {
    add('tractor_no_trailer', cap.label + ' is a tractor with no trailer - it cannot carry cargo. Pair it with a trailer in Combinations.');
  }
  const carried = !cap.tractorOnly;
  let warnedDeck = false; let warnedPayload = false; let warnedDeckH = false;
  const over = (a, b, f) => ' (' + f(a - b, U) + ' over)';
  jobs.forEach((j, i) => {
    const rule = ruleOf[i];
    const towed = !!(rule && rule.towed);
    if (rule) {
      const typeOk = (!rule.allowed_types.length && !rule.allowed_classes.length)
        || rule.allowed_types.includes(cap.type) || rule.allowed_types.includes(cap.truckType)
        || rule.allowed_classes.includes(cap.cls) || (towed && rule.allowed_classes.includes('tractor') && cap.tractorOnly);
      const what = j.label + ' (' + (j.commodity || 'notes') + ')';
      if (!typeOk) {
        add('cargo_needs_type', what + ': ' + rule.name + ' - ' + (rule.note || 'needs a different kind of unit') + ' ' + cap.label + ' is ' + articled(cap.typeLabel) + '.', j, rule.severity);
      } else if (rule.needs_any_feature.length && !rule.needs_any_feature.some((f) => cap.features.includes(f))) {
        add('cargo_needs_feature', what + ': ' + rule.name + ' needs ' + orList(rule.needs_any_feature.map(featureLabel)) + '; ' + cap.label + ' has ' + (cap.features.length ? cap.features.map(featureLabel).join(', ') : 'none of these ticked') + '.', j, rule.severity);
      }
    }
    // __TRUCK_REQUIRED__ the job needs a tarp / open deck truck (__OPEN_DECK__)
    if (j.truckRequired && !towed && !meetsTruckRequired(cap, j.truckRequired)) {
      add('truck_required', j.label + ' needs ' + articled(TRUCK_REQUIRED[j.truckRequired]) + ' (' + TRUCK_REQUIRED_WHAT[j.truckRequired] + '); ' + cap.label + ' is ' + articled(cap.typeLabel) + '.', j);
    }
    // __SELF_UNLOAD__ crane on the unit / a driver who can run the site's equipment
    if (j.selfUnload === 'crane' && !towed && !hasCrane(cap)) {
      add('self_unload_crane', j.label + ' must be self-unloaded with a truck crane; ' + cap.label + ' has no crane.', j);
    }
    if (j.selfUnload === 'onsite' && driverId && !driverCanSelfUnload(R, driverId)) {
      add('self_unload_operator', j.label + " must be unloaded by our driver with the site's equipment; " + (driverName ? driverName + ' is' : 'this driver is') + ' not set as able to (Drivers > Can self-unload).', j);
    }
    // __SITE_LOADING__ a stop at a site that loads only in ways this body / cover can't take
    if (!towed && carried) {
      siteLoadingIssues(cap, j.row, R, stops).forEach(([, msg]) => add('site_loading', j.label + ': ' + msg + ' (' + cap.label + ').', j));
      // __OPERATING_LIMITS__ no city runs / the most distance from the unit's home point, at every stop
      operatingIssues(cap, j.row, R, stops).forEach(([code, msg]) => add(code, j.label + ': ' + msg + '.', j));
    }
    if (towed || !carried) return;
    if (j.widthFt !== null && j.widthFt > G.legal_width_ft + 1e-9) {
      add('over_legal_width', j.label + ' is ' + fmtFt(j.widthFt, U) + ' wide - over the ' + fmtFt(G.legal_width_ft, U) + ' legal width' + over(j.widthFt, G.legal_width_ft, fmtFt) + ' (oversize permit needed).', j);
    }
    if (cap.cls !== 'bulk') {
      if ((cap.deckLengthFt === null || cap.deckWidthFt === null) && (j.lengthFt !== null || j.widthFt !== null)) {
        if (!warnedDeck) { warnedDeck = true; add('missing_unit_data', cap.label + ' has no ' + (cap.cls === 'enclosed' ? 'inside' : 'deck') + ' length/width on file - job sizes not checked.'); }
      }
      const allowance = cap.cls === 'open' ? G.open_deck_overhang_ft : 0;
      if (j.lengthFt !== null && cap.deckLengthFt !== null && j.lengthFt > cap.deckLengthFt + allowance + 1e-9) {
        add('too_long', j.label + ' is ' + fmtFt(j.lengthFt, U) + ' long; ' + cap.label + ' deck is ' + fmtFt(cap.deckLengthFt, U) + (allowance ? ' (+' + fmtFt(allowance, U) + ' overhang allowed)' : '') + over(j.lengthFt, cap.deckLengthFt + allowance, fmtFt) + '.', j);
      }
      if (j.widthFt !== null && cap.deckWidthFt !== null && j.widthFt > cap.deckWidthFt + 1e-9 && j.widthFt <= G.legal_width_ft + 1e-9) {
        if (cap.cls === 'enclosed' || cap.cls === 'pickup') add('too_wide', j.label + ' is ' + fmtFt(j.widthFt, U) + ' wide; ' + cap.label + ' is ' + fmtFt(cap.deckWidthFt, U) + ' wide inside' + over(j.widthFt, cap.deckWidthFt, fmtFt) + '.', j);
        else add('overhangs_sides', j.label + ' (' + fmtFt(j.widthFt, U) + ' wide) hangs over the ' + fmtFt(cap.deckWidthFt, U) + ' deck sides' + over(j.widthFt, cap.deckWidthFt, fmtFt) + ', within the legal width.', j);
      }
      if (j.heightFt !== null) {
        if (cap.maxCargoHeightFt !== null && j.heightFt > cap.maxCargoHeightFt + 1e-9) {
          add('too_tall', j.label + ' is ' + fmtFt(j.heightFt, U) + ' tall; ' + cap.label + ' is limited to ' + fmtFt(cap.maxCargoHeightFt, U) + ' for now' + over(j.heightFt, cap.maxCargoHeightFt, fmtFt) + '.', j);
        } else if (cap.cls === 'enclosed') {
          if (cap.insideHeightFt === null) { if (!warnedDeckH) { warnedDeckH = true; add('missing_unit_data', cap.label + ' has no inside height on file - job height not checked.'); } }
          else if (j.heightFt > cap.insideHeightFt + 1e-9) add('too_tall', j.label + ' is ' + fmtFt(j.heightFt, U) + ' tall; ' + cap.label + ' is ' + fmtFt(cap.insideHeightFt, U) + ' tall inside' + over(j.heightFt, cap.insideHeightFt, fmtFt) + '.', j);
        } else if (cap.cls === 'open') {
          if (cap.deckHeightFt === null) {
            if (j.heightFt > G.legal_height_ft + 1e-9) add('over_legal_height', j.label + ' is ' + fmtFt(j.heightFt, U) + ' tall - over the ' + fmtFt(G.legal_height_ft, U) + ' legal height on its own' + over(j.heightFt, G.legal_height_ft, fmtFt) + '.', j);
            else if (!warnedDeckH) { warnedDeckH = true; add('missing_unit_data', cap.label + ' has no deck height on file - travel height (deck + cargo) not checked.'); }
          } else if (cap.deckHeightFt + j.heightFt > G.legal_height_ft + 1e-9) {
            add('over_legal_height', j.label + ': ' + fmtFt(j.heightFt, U) + ' tall on a ' + fmtFt(cap.deckHeightFt, U) + ' deck = ' + fmtFt(cap.deckHeightFt + j.heightFt, U) + ' travel height, over the ' + fmtFt(G.legal_height_ft, U) + ' legal height' + over(cap.deckHeightFt + j.heightFt, G.legal_height_ft, fmtFt) + '.', j);
          }
        }
      }
    }
    if (j.weightLb === null) add('missing_load_data', j.label + ' has no weight - weight not checked.', j);
    else if (cap.payloadLb === null && cap.seasonPayloadLb === null) {
      if (!warnedPayload) { warnedPayload = true; add('missing_unit_data', cap.label + ' has no payload on file (enter payload, or GVWR and tare' + (unit.trailer ? ', or the tractor\'s GCWR' : '') + ') - weight not checked.'); }
    } else if (cap.payloadLb !== null && j.weightLb > cap.payloadLb + 1e-9) {
      add('over_payload', j.label + ' weighs ' + fmtLb(j.weightLb, U) + '; ' + cap.label + ' can carry ' + fmtLb(cap.payloadLb, U) + ' (' + cap.payloadFrom + ')' + over(j.weightLb, cap.payloadLb, fmtLb) + '.', j);
    } else if (cap.seasonPayloadLb !== null && j.weightLb > cap.seasonPayloadLb + 1e-9) {
      add('season_over_payload', j.label + ' weighs ' + fmtLb(j.weightLb, U) + '; half-load season (' + cap.season.start + ' to ' + cap.season.end + ') limits ' + cap.label + ' to ' + fmtLb(cap.seasonPayloadLb, U) + over(j.weightLb, cap.seasonPayloadLb, fmtLb) + seasonWhy(cap, U) + (cap.season.note ? ' - ' + cap.season.note : '') + '.', j);
    } else if (cap.payloadLb !== null && j.weightLb >= cap.payloadLb * R.near_capacity_pct / 100) {
      add('near_payload', j.label + ' is ' + Math.round(100 * j.weightLb / cap.payloadLb) + '% of ' + cap.label + '\'s ' + fmtLb(cap.payloadLb, U) + ' payload.', j);
    }
  });

  let peakLb = null; let peakNote = null;
  const carriedJobs = jobs.filter((j, i) => !(ruleOf[i] && ruleOf[i].towed));
  if (carried && carriedJobs.length > 1) {
    const r = routePeak(carriedJobs, stops);
    peakLb = r.peakLb;
    const why = r.ordered ? 'stop weights are not recorded, so each job counts at its full weight from its first pickup to its last drop-off'
      : 'no stop order yet - every job counted on board together';
    peakNote = 'Most weight on board at once ' + fmtLb(peakLb, U) + ' (' + why + ').';
    if (cap.payloadLb !== null && peakLb > cap.payloadLb + 1e-9) {
      add('route_over_payload', 'Route: up to ' + fmtLb(peakLb, U) + ' on board at once (' + r.atLabel + '); ' + cap.label + ' can carry ' + fmtLb(cap.payloadLb, U) + over(peakLb, cap.payloadLb, fmtLb) + '. ' + why.charAt(0).toUpperCase() + why.slice(1) + '.');
    } else if (cap.seasonPayloadLb !== null && peakLb > cap.seasonPayloadLb + 1e-9) {
      add('season_over_payload', 'Route: up to ' + fmtLb(peakLb, U) + ' on board at once (' + r.atLabel + '); half-load season limits ' + cap.label + ' to ' + fmtLb(cap.seasonPayloadLb, U) + over(peakLb, cap.seasonPayloadLb, fmtLb) + seasonWhy(cap, U) + '.');
    }
    if (cap.cls !== 'bulk' && cap.deckLengthFt !== null && r.peakLenFt > cap.deckLengthFt + (cap.cls === 'open' ? G.open_deck_overhang_ft : 0) + 1e-9) {
      add('route_deck_crowded', 'Route: jobs on board together add up to ' + fmtFt(r.peakLenFt, U) + ' of length on a ' + fmtFt(cap.deckLengthFt, U) + ' deck - check they fit side by side.');
    }
  }
  if (tm) { tm.issues.forEach((x) => add(x.code, x.message)); timingNote = tm.note; }
  return finish(issues, cap, peakLb, peakNote, timingNote, cap.season);
}

// __EQUIP_R5__ where a half-load limit comes from ('' = the fleet-wide rule, as before)
function seasonWhy(cap, U) {
  const f = cap && cap.seasonFrom;
  if (!f || f.kind === 'fleet') return '';
  return ' (' + f.who + ' half-load cap ' + fmtLb(f.grossLb, U) + ' gross'
    + (f.tareLb !== null && f.tareLb !== undefined ? ' - ' + fmtLb(f.tareLb, U) + ' tare)' : '; tare unknown, compared with the cargo alone)');
}

function finish(issues, cap, peakLb, peakNote, timingNote, season) {
  const by = (s) => issues.filter((x) => x.severity === s);
  const stops = by('stop'); const blocking = by('block'); const warnings = by('warn'); const infos = by('info');
  return {
    ok: !stops.length && !blocking.length && !warnings.length,
    canAssign: !stops.length,
    needsReason: blocking.length > 0,
    stops, blocking, warnings, infos, issues,
    unit: cap, peakLb, peakNote, timingNote, season: season || null,
  };
}

// Walk the stops: a job goes on at its FIRST pickup and comes off at its LAST
// drop-off (no per-stop weights exist). Without stops, everything is on at once.
export function routePeak(jobs, stops) {
  const byId = {}; jobs.forEach((j) => { if (j.id) byId[j.id] = j; });
  const seq = Array.isArray(stops) ? stops.filter((s) => s && byId[s.load_id]) : [];
  const total = (arr, k) => arr.reduce((a, j) => a + (j[k] || 0), 0);
  if (!seq.length) return { peakLb: total(jobs, 'weightLb'), peakLenFt: total(jobs, 'lengthFt'), ordered: false, atLabel: 'all jobs together' };
  const lastDrop = {}; const firstPick = {};
  seq.forEach((s, i) => {
    const t = String(s.stop_type || '').toLowerCase();
    if (t === 'delivery' || t === 'dropoff' || t === 'drop') lastDrop[s.load_id] = i;
    if (t === 'pickup' && firstPick[s.load_id] === undefined) firstPick[s.load_id] = i;
  });
  const on = new Set(jobs.filter((j) => j.id && firstPick[j.id] === undefined && seq.some((s) => s.load_id === j.id)).map((j) => j.id));
  let peakLb = 0; let peakLenFt = 0; let atLabel = 'start';
  const measure = (i) => {
    const arr = [...on].map((id) => byId[id]);
    const w = total(arr, 'weightLb'); const l = total(arr, 'lengthFt');
    if (w > peakLb) { peakLb = w; atLabel = i < 0 ? 'start of the route' : 'after stop ' + (i + 1); }
    if (l > peakLenFt) peakLenFt = l;
  };
  measure(-1);
  seq.forEach((s, i) => {
    if (firstPick[s.load_id] === i) on.add(s.load_id);
    measure(i);
    if (lastDrop[s.load_id] === i) on.delete(s.load_id);
  });
  return { peakLb, peakLenFt, ordered: true, atLabel };
}

// ---------------------------------------------------------------- timing / HOS
// __EQUIP_V2__ Henry: "A driver is already on a 7 hr route and I'm trying to add another
// 8 hr route - warn me about timing constraints, as they may not be able to run longer than
// 12 hrs. Warn about HOS rules when we build a route that violates, but no hard block."
//   existing   : { onDutyMin, driveMin, start, end, source }  his other work that day
//   added      : { onDutyMin, driveMin, start, end, source }  what is being assigned
//   prevDayEnd : ISO end of his previous day's work (for the 8 h off), optional
//   settings   : company_day_hours (12), hos_driving_hours (13), hos_on_duty_hours (14),
//                hos_window_hours (16), hos_off_hours (8)   - Canada federal, south of 60
// start / end are ISO times; any of them may be missing (that check is then skipped).
// -> { issues: [{ code, message }], note, totals }
// a time with no zone is read as UTC (as the database's naive times are), in both copies
const tms = (s) => {
  if (!s) return null;
  let v = String(s).trim().replace(' ', 'T');
  if (/^\d{4}-\d{2}-\d{2}T/.test(v) && !/([zZ]|[+-]\d\d:?\d\d)$/.test(v)) v += 'Z';
  const t = Date.parse(v); return Number.isFinite(t) ? t : null;
};
export function timingIssues({ existing, added, prevDayEnd, settings, driverName } = {}) {
  const S = { ...DEFAULT_TIMING, ...(settings || {}) };
  const ex = existing || {}; const ad = added || {};
  const exOn = Math.max(0, num(ex.onDutyMin) || 0); const adOn = Math.max(0, num(ad.onDutyMin) || 0);
  const exDr = Math.max(0, num(ex.driveMin) || 0); const adDr = Math.max(0, num(ad.driveMin) || 0);
  const onDuty = exOn + adOn; const drive = exDr + adDr;
  const who = driverName || 'Driver';
  const rough = (ex.source === 'rough' || ad.source === 'rough') ? ' (rough estimate)' : '';
  const note = who + ': ' + fmtH(exOn) + ' already + ' + fmtH(adOn) + ' = ' + fmtH(onDuty) + ' on duty' + (drive ? ', ' + fmtH(drive) + ' driving' : '') + rough + '.';
  const issues = [];
  const push = (code, msg) => issues.push({ code, message: note.replace(/\.$/, '') + ' - ' + msg + '.' });
  if (onDuty > S.company_day_hours * 60 + 0.5) push('over_company_day', 'over the ' + S.company_day_hours + ' h company limit');
  if (onDuty > S.hos_on_duty_hours * 60 + 0.5) push('hos_on_duty', 'over the ' + S.hos_on_duty_hours + ' h on-duty limit (HOS)');
  if (drive > S.hos_driving_hours * 60 + 0.5) push('hos_driving', 'over the ' + S.hos_driving_hours + ' h driving limit (HOS)');
  const es = tms(ex.start); const ee = tms(ex.end); const as = tms(ad.start); const ae = tms(ad.end);
  if (es !== null && ee !== null && as !== null && ae !== null) {
    const ov = Math.min(ee, ae) - Math.max(es, as);
    if (ov > 60000) push('timing_overlap', 'the new work overlaps his other work by ' + Math.round(ov / 60000) + ' min');
    const elapsed = (Math.max(ee, ae) - Math.min(es, as)) / 60000;
    if (elapsed > S.hos_window_hours * 60 + 0.5) push('hos_window', 'first start to last finish is ' + fmtH(elapsed) + ', over the ' + S.hos_window_hours + ' h window (HOS)');
  }
  const starts = [es, as].filter((x) => x !== null);
  const pe = tms(prevDayEnd);
  if (pe !== null && starts.length) {
    const off = (Math.min(...starts) - pe) / 60000;
    if (off >= 0 && off < S.hos_off_hours * 60 - 0.5) push('hos_off', 'only ' + fmtH(off) + ' off since his previous day ended, under the ' + S.hos_off_hours + ' h off (HOS)');
  }
  return { issues, note, totals: { onDutyMin: onDuty, driveMin: drive } };
}

// The override written onto each job and into the audit log when someone assigns
// anyway: { reason (may be blank for warnings), accepted: [codes], issues, needs_reason, unit, at, context }.
export function buildOverride({ result, reason, by, context, driverId }) {
  const r = String(reason || '').trim();
  const list = ((result && result.blocking) || []).concat((result && result.warnings) || []);
  return {
    reason: r,
    needs_reason: !!(result && result.needsReason),
    accepted: Array.from(new Set(list.map((x) => x.code))),
    driver_id: driverId || null,
    unit: result && result.unit ? result.unit.label : null,
    issues: list.map((x) => ({ code: x.code, severity: x.severity, message: x.message })),
    timing: result && result.timingNote ? result.timingNote : null,
    by: by || null,
    at: new Date().toISOString(),
    context: context || null,
  };
}
export const OVERRIDE_MIN_REASON = 4;
